import React, { useState, useEffect } from "react";
import { useFormik, FieldArray, FormikProvider } from "formik";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import Breadcrums from "../../common/breadcrumbs";
import {
  globalLoader,
  handleServerValidations,
} from "../../../../utils/commonfunction";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { Update, Details, DeleteImage } from "../../services/product.services";
import * as productAttributeServices from "../../services/productAttribute.services";
import * as productAttributeValueServices from "../../services/productAttributeValue.services";
import * as productCategoryServices from "../../services/productCategory.services";
import CustomCkeditor from "../../common/customeditor";
import TagInput from "../../common/tagInput";

const defaultSubProductItem = {
  id: null,
  name: "",
  price: "",
  price_usd: "",
  mrp_inr: "",
  mrp_usd: "",
  certificate: null,
  certificate_file: null,
  keywords: "",
  discount_percentage: "0",
  total_stock: "",
  status: 1,
  is_returnable: false,
  weight: "",
  width: "",
  height: "",
  length: "",
  description: "",
  about_description: "",
  attributes: [{ attribute_id: "", attribute_value_id: "" }],
  existingImages: [],
  images: [],
  previewImages: [],
};

const EditProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [attributes, setAttributes] = useState([]);
  const [attributeValues, setAttributeValues] = useState({});
  const [categories, setCategories] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [initialData, setInitialData] = useState({
    name: "",
    product_category_id: "",
    sub_products: [{ ...defaultSubProductItem }],
  });

  const breadcrumbs = [
    { title: t("link_dashboard"), url: "/admin/dashboard" },
    { title: "Products", url: "/admin/product/list/1" },
    { title: t("btn_edit"), url: "" },
  ];

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    return `${process.env.REACT_APP_API_URL || ""}/${imagePath}`;
  };

  useEffect(() => {
    // Fetch Attributes
    const formData = new FormData();
    formData.append("page", 1);
    formData.append("per_page", 100);
    formData.append("search", JSON.stringify({ status: 1 }));

    productAttributeServices.List(formData).then((res) => {
      if (res.success) {
        setAttributes(res.data.list || []);
      }
    });

    // Fetch Categories
    productCategoryServices
      .List({ search: JSON.stringify({ status: 1 }), page: 1, per_page: 100 })
      .then((res) => {
        if (res.success) {
          setCategories(res.data.list || []);
        }
      });

    // Fetch Product Details
    if (id) {
      globalLoader(true);
      Details(id)
        .then(async (res) => {
          if (res.success) {
            const data = res.data;
            const mappedSubProducts =
              data.sub_products && data.sub_products.length > 0
                ? data.sub_products.map((sub) => ({
                    id: sub.id ? Number(sub.id) : null,
                    name: sub.name || "",
                    price: sub.price ?? "",
                    price_usd: sub.price_usd ?? "",
                    mrp_inr: sub.mrp_inr ?? sub.mrp ?? "",
                    mrp_usd: sub.mrp_usd ?? "",
                    certificate: sub.certificate || "",
                    certificate_file: null,
                    keywords: sub.keywords || "",
                    discount_percentage: sub.discount_percentage ?? 0,
                    total_stock: sub.total_stock ?? "",
                    status:
                      sub.status === true || sub.status === 1 || sub.status === "1"
                        ? 1
                        : 0,
                    is_returnable: !!sub.is_returnable,
                    weight: sub.weight ?? "",
                    width: sub.width ?? "",
                    height: sub.height ?? "",
                    length: sub.length ?? "",
                    description: sub.description || "",
                    about_description: sub.about_description || "",
                    attributes:
                      sub.attributes && sub.attributes.length > 0
                        ? sub.attributes.map((a) => ({
                            attribute_id: a.attribute_id ? String(a.attribute_id) : "",
                            attribute_value_id: a.attribute_value_id ? String(a.attribute_value_id) : "",
                          }))
                        : [{ attribute_id: "", attribute_value_id: "" }],
                    existingImages: sub.images || [],
                    images: [],
                    previewImages: [],
                  }))
                : [{ ...defaultSubProductItem }];

            // Collect all unique attribute IDs across all sub products
            const attrIds = new Set();
            mappedSubProducts.forEach((sub) => {
              sub.attributes.forEach((attr) => {
                if (attr.attribute_id) {
                  attrIds.add(String(attr.attribute_id));
                }
              });
            });

            // Fetch attribute values for all existing attribute IDs concurrently BEFORE enabling render
            const attrValuesMap = {};
            if (attrIds.size > 0) {
              await Promise.all(
                Array.from(attrIds).map((attrId) =>
                  productAttributeValueServices
                    .GetAttributeValues({ attributeId: attrId })
                    .then((valRes) => {
                      if (valRes && valRes.success) {
                        attrValuesMap[attrId] = valRes.data || [];
                      }
                    })
                    .catch((err) => console.error(err))
                )
              );
            }

            setAttributeValues((prev) => ({ ...prev, ...attrValuesMap }));

            setInitialData({
              name: data.name || "",
              product_category_id: data.product_category_id
                ? String(data.product_category_id)
                : "",
              gst_percent: data.gst_percent || 0,
              sub_products: mappedSubProducts,
            });
          }
          setIsLoaded(true);
          globalLoader(false);
        })
        .catch((err) => {
          console.error(err);
          globalLoader(false);
        });
    }
  }, [id]);

  const fetchAttributeValues = (attributeId) => {
    if (!attributeId || attributeValues[attributeId]) return;

    productAttributeValueServices
      .GetAttributeValues({ attributeId: String(attributeId) })
      .then((res) => {
        if (res && res.success) {
          setAttributeValues((prev) => ({
            ...prev,
            [String(attributeId)]: res.data || [],
          }));
        }
      })
      .catch((err) => {
        console.error("Error fetching attribute values:", err);
      });
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required("Name is required")
      .max(255, "Name cannot exceed 255 characters"),
    product_category_id: Yup.string().required("Category is required"),
    gst_percent: Yup.number().typeError("GST percent must be a number").optional().min(0, "GST percent cannot be negative").max(100, "GST percent cannot exceed 100"),
    sub_products: Yup.array()
      .of(
        Yup.object().shape({
          price: Yup.number()
            .required("Price is required")
            .moreThan(0, "Price must be greater than 0"),
          price_usd: Yup.number()
            .required("USD Price is required")
            .moreThan(0, "USD Price must be greater than 0"),
          discount_percentage: Yup.number()
            .required("Discount percentage is required")
            .min(0, "Discount cannot be negative")
            .max(100, "Discount cannot exceed 100"),
          total_stock: Yup.number()
            .required("Total stock is required")
            .min(0, "Stock cannot be negative"),
          weight: Yup.number()
            .required("Weight is required")
            .min(0, "Weight cannot be negative"),
          width: Yup.number()
            .required("Width is required")
            .min(0, "Width cannot be negative"),
          height: Yup.number()
            .required("Height is required")
            .min(0, "Height cannot be negative"),
          length: Yup.number()
            .required("Length is required")
            .min(0, "Length cannot be negative"),
          description: Yup.string().trim().required("Short description is required"),
          about_description: Yup.string().trim().required("About description is required"),
          attributes: Yup.array()
            .of(
              Yup.object().shape({
                attribute_id: Yup.string().required("Attribute is required"),
                attribute_value_id: Yup.string().required("Value is required"),
              })
            )
            .test(
              "unique-attribute",
              "Duplicate attribute is not allowed in the same sub-product",
              function (attrs) {
                if (!attrs) return true;
                const ids = attrs.map((a) => String(a.attribute_id)).filter(Boolean);
                const uniqueIds = new Set(ids);
                return ids.length === uniqueIds.size;
              }
            )
            .min(1, "At least one attribute is required for each sub-product"),
        })
      )
      .min(1, "At least one sub-product variant is required"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialData,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      globalLoader(true);
      setSubmitted(true);

      // Check duplicate attribute combinations among sub-products
      const seenSets = new Set();
      let duplicateFound = false;
      for (const sub of values.sub_products) {
        const key = (sub.attributes || [])
          .filter((a) => a.attribute_id && a.attribute_value_id)
          .map((a) => `${a.attribute_id}:${a.attribute_value_id}`)
          .sort()
          .join("|") || "EMPTY";

        if (seenSets.has(key)) {
          duplicateFound = true;
          break;
        }
        seenSets.add(key);
      }

      if (duplicateFound) {
        globalLoader(false);
        setSubmitted(false);
        Swal.fire({
          icon: "error",
          text: "Each sub-product must have a unique combination of attributes. Two sub-products cannot have the exact same attribute values.",
          ...SWAL_SETTINGS,
        });
        return;
      }

      const formData = new FormData();
      formData.append("id", id);
      formData.append("name", values.name);
      formData.append("product_category_id", values.product_category_id);
      formData.append("gst_percent", values.gst_percent || 0);

      const cleanedSubProducts = values.sub_products.map((sub, idx) => ({
        id: sub.id || null,
        price: sub.price,
        price_usd: sub.price_usd,
        mrp_inr: sub.mrp_inr || 0,
        mrp_usd: sub.mrp_usd || 0,
        keywords: sub.keywords || "",
        discount_percentage: sub.discount_percentage,
        total_stock: sub.total_stock,
        status: sub.status,
        is_returnable: sub.is_returnable ? 1 : 0,
        weight: sub.weight,
        width: sub.width,
        height: sub.height,
        length: sub.length,
        description: sub.description,
        about_description: sub.about_description,
        attributes: sub.attributes.map((a) => ({
          attribute_id: a.attribute_id,
          attribute_value_id: a.attribute_value_id,
        })),
      }));

      formData.append("sub_products", JSON.stringify(cleanedSubProducts));

      values.sub_products.forEach((sub, subIdx) => {
        if (sub.images && sub.images.length > 0) {
          sub.images.forEach((file) => {
            formData.append(`sub_product_images_${subIdx}`, file);
          });
        }
        if (sub.certificate_file) {
          formData.append(`sub_product_certificate_${subIdx}`, sub.certificate_file);
        }
      });

      Update(formData)
        .then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            globalLoader(false);
            setTimeout(() => {
              navigate(`/admin/product/list/1`);
            }, 1000);
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
            globalLoader(false);
            setSubmitted(false);
          }
        })
        .catch((error) => {
          console.log("error ====> ", error);
          globalLoader(false);
          setSubmitted(false);
        });
    },
  });

  const handleSubImageChange = (e, subIndex) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const currentSub = formik.values.sub_products[subIndex];
      const newImages = [...(currentSub.images || []), ...files];
      formik.setFieldValue(`sub_products.${subIndex}.images`, newImages);

      const newPreviews = files.map((file) => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (ev) => resolve(ev.target.result);
          reader.readAsDataURL(file);
        });
      });

      Promise.all(newPreviews).then((previews) => {
        const existingPreviews = currentSub.previewImages || [];
        formik.setFieldValue(`sub_products.${subIndex}.previewImages`, [
          ...existingPreviews,
          ...previews,
        ]);
      });
    }
  };

  const removeSubNewImage = (subIndex, imgIndex) => {
    const currentSub = formik.values.sub_products[subIndex];
    const newImages = [...currentSub.images];
    newImages.splice(imgIndex, 1);
    formik.setFieldValue(`sub_products.${subIndex}.images`, newImages);

    const newPreviews = [...currentSub.previewImages];
    newPreviews.splice(imgIndex, 1);
    formik.setFieldValue(`sub_products.${subIndex}.previewImages`, newPreviews);
  };

  const handleDeleteExistingSubImage = (subIndex, imageId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      ...SWAL_SETTINGS,
    }).then((result) => {
      if (result.isConfirmed) {
        globalLoader(true);
        DeleteImage({ id: imageId })
          .then((res) => {
            if (res.success) {
              Swal.fire({
                icon: "success",
                text: res.message,
                ...SWAL_SETTINGS,
              });

              const currentSub = formik.values.sub_products[subIndex];
              const updatedExisting = currentSub.existingImages.filter(
                (img) => img.id !== imageId
              );
              formik.setFieldValue(
                `sub_products.${subIndex}.existingImages`,
                updatedExisting
              );
            } else {
              Swal.fire({
                icon: "error",
                text: res.message,
                ...SWAL_SETTINGS,
              });
            }
            globalLoader(false);
          })
          .catch((err) => {
            console.error(err);
            globalLoader(false);
          });
      }
    });
  };

  if (!isLoaded) {
    return null;
  }

  return (
    <FormikProvider value={formik}>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card">
          <div className="card-body">
            <h6 className="main-content-label mb-3">Edit Product</h6>
            <form onSubmit={formik.handleSubmit}>
              <div className="row row-sm">
                {/* Product Name */}
                <div className="col-lg-6 form-group">
                  <label htmlFor="name" className="text-left d-flex">
                    Product Name :<span className="requirestar">*</span>
                  </label>
                  <input
                    name="name"
                    id="name"
                    type="text"
                    className="form-control"
                    placeholder="Enter product name"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.name}
                  />
                  {formik.errors.name && formik.touched.name && (
                    <span className="text-danger d-flex text-left">
                      {formik.errors.name}
                    </span>
                  )}
                </div>

                {/* Product Category */}
                <div className="col-lg-6 form-group">
                  <label
                    htmlFor="product_category_id"
                    className="text-left d-flex"
                  >
                    Category :<span className="requirestar">*</span>
                  </label>
                  <select
                    name="product_category_id"
                    id="product_category_id"
                    className="form-control"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.product_category_id}
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.title}
                      </option>
                    ))}
                  </select>
                  {formik.errors.product_category_id &&
                    formik.touched.product_category_id && (
                      <span className="text-danger d-flex text-left">
                        {formik.errors.product_category_id}
                      </span>
                    )}
                </div>

                {/* GST Percent */}
                <div className="col-lg-6 form-group">
                  <label htmlFor="gst_percent" className="text-left d-flex">
                    GST (%) (India Only) :
                  </label>
                  <input
                    name="gst_percent"
                    id="gst_percent"
                    type="number"
                    className="form-control"
                    placeholder="0"
                    min="0"
                    max="100"
                    step="0.01"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.gst_percent}
                  />
                  {formik.errors.gst_percent && formik.touched.gst_percent && (
                    <span className="text-danger d-flex text-left">
                      {formik.errors.gst_percent}
                    </span>
                  )}
                </div>

                {/* Sub-Products Dynamic Array */}
                <div className="col-lg-12 mt-4">
                  <h6 className="main-content-label mb-3">Sub-Products & Variant Combinations</h6>
                  <FieldArray
                    name="sub_products"
                    render={(subHelpers) => (
                      <div>
                        {formik.values.sub_products.map((sub, subIdx) => (
                          <div
                            key={subIdx}
                            className="card border p-3 mb-4 shadow-sm rounded position-relative"
                            style={{ backgroundColor: "#f8fafc", borderColor: "#cbd5e1" }}
                          >
                            <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                              <h6 className="mb-0 text-primary fw-bold">
                                <i className="fa fa-cubes me-2"></i>Sub-Product #{subIdx + 1}
                              </h6>
                              {formik.values.sub_products.length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => subHelpers.remove(subIdx)}
                                >
                                  <i className="fa fa-trash me-1"></i> Remove Sub-Product
                                </button>
                              )}
                            </div>

                            <div className="row row-sm">
                              {/* Nested Attributes List for this Sub Product */}
                              <div className="col-lg-12 form-group mb-3">
                                <label className="text-left d-flex fw-bold text-dark mb-2">
                                  Attributes :<span className="requirestar">*</span>
                                </label>
                                <FieldArray
                                  name={`sub_products.${subIdx}.attributes`}
                                  render={(attrHelpers) => (
                                    <div className="bg-white p-3 border rounded">
                                      {sub.attributes.map((attr, attrIdx) => {
                                        const availableValues = (
                                          attributeValues[attr.attribute_id] || []
                                        ).filter(
                                          (v) =>
                                            !sub.attributes.some(
                                              (other, oIdx) =>
                                                oIdx !== attrIdx &&
                                                String(other.attribute_id) === String(attr.attribute_id) &&
                                                String(other.attribute_value_id) === String(v.id)
                                            )
                                        );

                                        return (
                                          <div
                                            key={attrIdx}
                                            className="row row-sm mb-2 align-items-end"
                                          >
                                            {/* Attribute selection - shows ALL attributes */}
                                            <div className="col-lg-5">
                                              <label className="text-left d-flex small">Attribute :</label>
                                              <select
                                                name={`sub_products.${subIdx}.attributes.${attrIdx}.attribute_id`}
                                                className="form-control"
                                                value={attr.attribute_id}
                                                onChange={(e) => {
                                                  formik.handleChange(e);
                                                  formik.setFieldValue(
                                                    `sub_products.${subIdx}.attributes.${attrIdx}.attribute_value_id`,
                                                    ""
                                                  );
                                                  fetchAttributeValues(e.target.value);
                                                }}
                                              >
                                                <option value="">Select Attribute</option>
                                                {attributes.map((a) => {
                                                  const isAlreadySelected = sub.attributes.some(
                                                    (other, oIdx) =>
                                                      oIdx !== attrIdx &&
                                                      String(other.attribute_id) === String(a.id)
                                                  );
                                                  return (
                                                    <option
                                                      key={a.id}
                                                      value={String(a.id)}
                                                      disabled={isAlreadySelected}
                                                    >
                                                      {a.title} {isAlreadySelected ? "(Already Selected)" : ""}
                                                    </option>
                                                  );
                                                })}
                                              </select>
                                              {formik.errors.sub_products?.[subIdx]?.attributes?.[attrIdx]?.attribute_id &&
                                                formik.touched.sub_products?.[subIdx]?.attributes?.[attrIdx]?.attribute_id && (
                                                  <span className="text-danger d-flex text-left small mt-1">
                                                    {formik.errors.sub_products[subIdx].attributes[attrIdx].attribute_id}
                                                  </span>
                                                )}
                                            </div>

                                            {/* Value selection - excludes already selected values for this attribute */}
                                            <div className="col-lg-5">
                                              <label className="text-left d-flex small">Value :</label>
                                              <select
                                                name={`sub_products.${subIdx}.attributes.${attrIdx}.attribute_value_id`}
                                                className="form-control"
                                                value={attr.attribute_value_id}
                                                onChange={formik.handleChange}
                                              >
                                                <option value="">Select Value</option>
                                                {availableValues.map((v) => (
                                                  <option key={v.id} value={String(v.id)}>
                                                    {v.value}
                                                  </option>
                                                ))}
                                              </select>
                                              {formik.errors.sub_products?.[subIdx]?.attributes?.[attrIdx]?.attribute_value_id &&
                                                formik.touched.sub_products?.[subIdx]?.attributes?.[attrIdx]?.attribute_value_id && (
                                                  <span className="text-danger d-flex text-left small mt-1">
                                                    {formik.errors.sub_products[subIdx].attributes[attrIdx].attribute_value_id}
                                                  </span>
                                                )}
                                            </div>
                                            <div className="col-lg-2">
                                              <button
                                                type="button"
                                                className="btn btn-outline-danger btn-sm"
                                                onClick={() => attrHelpers.remove(attrIdx)}
                                                disabled={sub.attributes.length === 1}
                                              >
                                                <i className="fa fa-trash"></i>
                                              </button>
                                            </div>
                                          </div>
                                        );
                                      })}
                                      <button
                                        type="button"
                                        className="btn btn-secondary btn-sm mt-2"
                                        onClick={() =>
                                          attrHelpers.push({ attribute_id: "", attribute_value_id: "" })
                                        }
                                      >
                                        <i className="fa fa-plus me-1"></i> Add Attribute to Sub-Product
                                      </button>
                                    </div>
                                  )}
                                />
                              </div>

                              {/* Price */}
                              <div className="col-lg-3 form-group">
                                <label className="text-left d-flex">
                                  Price (₹) :<span className="requirestar">*</span>
                                </label>
                                <input
                                  name={`sub_products.${subIdx}.price`}
                                  type="number"
                                  className="form-control"
                                  placeholder="0.00"
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  value={sub.price}
                                />
                                {formik.errors.sub_products?.[subIdx]?.price &&
                                  formik.touched.sub_products?.[subIdx]?.price && (
                                    <span className="text-danger d-flex text-left mt-1">
                                      {formik.errors.sub_products[subIdx].price}
                                    </span>
                                  )}
                              </div>

                              {/* Price USD */}
                              <div className="col-lg-3 form-group">
                                <label className="text-left d-flex">
                                  Price (USD $) :<span className="requirestar">*</span>
                                </label>
                                <input
                                  name={`sub_products.${subIdx}.price_usd`}
                                  type="number"
                                  className="form-control"
                                  placeholder="0.00"
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  value={sub.price_usd}
                                />
                                {formik.errors.sub_products?.[subIdx]?.price_usd &&
                                  formik.touched.sub_products?.[subIdx]?.price_usd && (
                                    <span className="text-danger d-flex text-left mt-1">
                                      {formik.errors.sub_products[subIdx].price_usd}
                                    </span>
                                  )}
                              </div>

                              {/* MRP INR */}
                              <div className="col-lg-3 form-group">
                                <label className="text-left d-flex">
                                  MRP (₹) :
                                </label>
                                <input
                                  name={`sub_products.${subIdx}.mrp_inr`}
                                  type="number"
                                  className="form-control"
                                  placeholder="0.00"
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  value={sub.mrp_inr}
                                />
                              </div>

                              {/* MRP USD */}
                              <div className="col-lg-3 form-group">
                                <label className="text-left d-flex">
                                  MRP (USD $) :
                                </label>
                                <input
                                  name={`sub_products.${subIdx}.mrp_usd`}
                                  type="number"
                                  className="form-control"
                                  placeholder="0.00"
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  value={sub.mrp_usd}
                                />
                              </div>

                              {/* Discount */}
                              <div className="col-lg-3 form-group">
                                <label className="text-left d-flex">
                                  Discount (%) :<span className="requirestar">*</span>
                                </label>
                                <input
                                  name={`sub_products.${subIdx}.discount_percentage`}
                                  type="number"
                                  min="0"
                                  max="100"
                                  className="form-control"
                                  placeholder="0"
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  value={sub.discount_percentage}
                                />
                                {formik.errors.sub_products?.[subIdx]?.discount_percentage &&
                                  formik.touched.sub_products?.[subIdx]?.discount_percentage && (
                                    <span className="text-danger d-flex text-left mt-1">
                                      {formik.errors.sub_products[subIdx].discount_percentage}
                                    </span>
                                  )}
                              </div>

                              {/* Total Stock */}
                              <div className="col-lg-3 form-group">
                                <label className="text-left d-flex">
                                  Total Stock :<span className="requirestar">*</span>
                                </label>
                                <input
                                  name={`sub_products.${subIdx}.total_stock`}
                                  type="number"
                                  className="form-control"
                                  placeholder="0"
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  value={sub.total_stock}
                                />
                                {formik.errors.sub_products?.[subIdx]?.total_stock &&
                                  formik.touched.sub_products?.[subIdx]?.total_stock && (
                                    <span className="text-danger d-flex text-left mt-1">
                                      {formik.errors.sub_products[subIdx].total_stock}
                                    </span>
                                  )}
                              </div>

                              {/* Status */}
                              <div className="col-lg-3 form-group">
                                <label className="text-left d-flex">Status :</label>
                                <select
                                  name={`sub_products.${subIdx}.status`}
                                  className="form-control"
                                  onChange={formik.handleChange}
                                  value={sub.status}
                                >
                                  <option value={1}>{t("Active")}</option>
                                  <option value={0}>{t("Inactive")}</option>
                                </select>
                              </div>

                              {/* Is Returnable */}
                              <div className="col-lg-3 form-group">
                                <label className="text-left d-flex">Is Returnable :</label>
                                <select
                                  name={`sub_products.${subIdx}.is_returnable`}
                                  className="form-control"
                                  onChange={(e) =>
                                    formik.setFieldValue(
                                      `sub_products.${subIdx}.is_returnable`,
                                      e.target.value === "true"
                                    )
                                  }
                                  value={sub.is_returnable ? "true" : "false"}
                                >
                                  <option value="true">Yes</option>
                                  <option value="false">No</option>
                                </select>
                              </div>

                              {/* Weight */}
                              <div className="col-lg-3 form-group">
                                <label className="text-left d-flex">
                                  Weight (kg) :<span className="requirestar">*</span>
                                </label>
                                <input
                                  name={`sub_products.${subIdx}.weight`}
                                  type="number"
                                  className="form-control"
                                  placeholder="0.00"
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  value={sub.weight}
                                />
                                {formik.errors.sub_products?.[subIdx]?.weight &&
                                  formik.touched.sub_products?.[subIdx]?.weight && (
                                    <span className="text-danger d-flex text-left mt-1">
                                      {formik.errors.sub_products[subIdx].weight}
                                    </span>
                                  )}
                              </div>

                              {/* Width */}
                              <div className="col-lg-3 form-group">
                                <label className="text-left d-flex">
                                  Width (cm) :<span className="requirestar">*</span>
                                </label>
                                <input
                                  name={`sub_products.${subIdx}.width`}
                                  type="number"
                                  className="form-control"
                                  placeholder="0.00"
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  value={sub.width}
                                />
                                {formik.errors.sub_products?.[subIdx]?.width &&
                                  formik.touched.sub_products?.[subIdx]?.width && (
                                    <span className="text-danger d-flex text-left mt-1">
                                      {formik.errors.sub_products[subIdx].width}
                                    </span>
                                  )}
                              </div>

                              {/* Height */}
                              <div className="col-lg-3 form-group">
                                <label className="text-left d-flex">
                                  Height (cm) :<span className="requirestar">*</span>
                                </label>
                                <input
                                  name={`sub_products.${subIdx}.height`}
                                  type="number"
                                  className="form-control"
                                  placeholder="0.00"
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  value={sub.height}
                                />
                                {formik.errors.sub_products?.[subIdx]?.height &&
                                  formik.touched.sub_products?.[subIdx]?.height && (
                                    <span className="text-danger d-flex text-left mt-1">
                                      {formik.errors.sub_products[subIdx].height}
                                    </span>
                                  )}
                              </div>

                              {/* Length */}
                              <div className="col-lg-3 form-group">
                                <label className="text-left d-flex">
                                  Length (cm) :<span className="requirestar">*</span>
                                </label>
                                <input
                                  name={`sub_products.${subIdx}.length`}
                                  type="number"
                                  className="form-control"
                                  placeholder="0.00"
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  value={sub.length}
                                />
                                {formik.errors.sub_products?.[subIdx]?.length &&
                                  formik.touched.sub_products?.[subIdx]?.length && (
                                    <span className="text-danger d-flex text-left mt-1">
                                      {formik.errors.sub_products[subIdx].length}
                                    </span>
                                  )}
                              </div>

                              {/* Certificate (Optional) */}
                              <div className="col-lg-6 form-group">
                                <label className="text-left d-flex">
                                  Certificate (Optional) :
                                </label>
                                {sub.certificate && (
                                  <div className="mb-1">
                                    <small className="text-muted">
                                      Existing:{" "}
                                      <a
                                        href={getImageUrl(sub.certificate)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        View Certificate
                                      </a>
                                    </small>
                                  </div>
                                )}
                                <input
                                  type="file"
                                  accept="image/*,application/pdf"
                                  className="form-control"
                                  onChange={(e) => {
                                    if (e.target.files && e.target.files[0]) {
                                      formik.setFieldValue(`sub_products.${subIdx}.certificate_file`, e.target.files[0]);
                                    }
                                  }}
                                />
                              </div>

                              {/* Keywords */}
                              <div className="col-lg-6 form-group">
                                <label className="text-left d-flex">
                                  Keywords (Multiple) :
                                </label>
                                <TagInput
                                  value={sub.keywords}
                                  onChange={(newKeywords) =>
                                    formik.setFieldValue(`sub_products.${subIdx}.keywords`, newKeywords)
                                  }
                                  placeholder="Type keyword & press Enter or comma"
                                />
                              </div>

                              {/* Sub-Product Images */}
                              <div className="col-lg-12 form-group">
                                <label className="text-left d-flex">
                                  Sub-Product Images :
                                </label>

                                {sub.existingImages && sub.existingImages.length > 0 && (
                                  <div className="mb-2">
                                    <small className="text-muted d-block mb-1">Existing Images:</small>
                                    <div className="d-flex flex-wrap gap-2">
                                      {sub.existingImages.map((img) => (
                                        <div key={img.id} className="position-relative">
                                          <img
                                            src={getImageUrl(img.image)}
                                            alt="Existing Sub Img"
                                            style={{
                                              width: "80px",
                                              height: "80px",
                                              objectFit: "cover",
                                              borderRadius: "8px",
                                              border: "1px solid #dee2e6",
                                            }}
                                          />
                                          <button
                                            type="button"
                                            className="btn btn-danger btn-sm position-absolute"
                                            style={{
                                              top: "-5px",
                                              right: "-5px",
                                              borderRadius: "50%",
                                              padding: "2px 6px",
                                            }}
                                            onClick={() =>
                                              handleDeleteExistingSubImage(subIdx, img.id)
                                            }
                                          >
                                            &times;
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                <input
                                  type="file"
                                  multiple
                                  className="form-control"
                                  accept="image/*"
                                  onChange={(e) => handleSubImageChange(e, subIdx)}
                                />
                                <div className="d-flex flex-wrap gap-2 mt-2">
                                  {(sub.previewImages || []).map((src, imgIdx) => (
                                    <div key={imgIdx} className="position-relative">
                                      <img
                                        src={src}
                                        alt={`Sub ${subIdx} preview ${imgIdx}`}
                                        style={{
                                          width: "80px",
                                          height: "80px",
                                          objectFit: "cover",
                                          borderRadius: "8px",
                                          border: "1px solid #dee2e6",
                                        }}
                                      />
                                      <button
                                        type="button"
                                        className="btn btn-danger btn-sm position-absolute"
                                        style={{
                                          top: "-5px",
                                          right: "-5px",
                                          borderRadius: "50%",
                                          padding: "2px 6px",
                                        }}
                                        onClick={() => removeSubNewImage(subIdx, imgIdx)}
                                      >
                                        &times;
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Short Description */}
                              <div className="col-lg-12 form-group">
                                <label className="text-left d-flex">
                                  Short Description :<span className="requirestar">*</span>
                                </label>
                                <textarea
                                  name={`sub_products.${subIdx}.description`}
                                  className="form-control"
                                  placeholder="Enter short description for this sub-product variant"
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  value={sub.description}
                                  rows={2}
                                />
                                {formik.errors.sub_products?.[subIdx]?.description &&
                                  formik.touched.sub_products?.[subIdx]?.description && (
                                    <span className="text-danger d-flex text-left mt-1">
                                      {formik.errors.sub_products[subIdx].description}
                                    </span>
                                  )}
                              </div>

                              {/* Detailed Description */}
                              <div className="col-lg-12 form-group">
                                <label className="text-left d-flex">
                                  About Product (Detailed) :<span className="requirestar">*</span>
                                </label>
                                <CustomCkeditor
                                  fieldname={`sub_products.${subIdx}.about_description`}
                                  setFieldValue={formik.setFieldValue}
                                  value={sub.about_description}
                                  setFieldTouched={formik.setFieldTouched}
                                />
                                {formik.errors.sub_products?.[subIdx]?.about_description &&
                                  formik.touched.sub_products?.[subIdx]?.about_description && (
                                    <span className="text-danger d-flex text-left mt-1">
                                      {formik.errors.sub_products[subIdx].about_description}
                                    </span>
                                  )}
                              </div>
                            </div>
                          </div>
                        ))}

                        <button
                          type="button"
                          className="btn btn-primary mt-2"
                          onClick={() =>
                            subHelpers.push({
                              ...defaultSubProductItem,
                              attributes: [{ attribute_id: "", attribute_value_id: "" }],
                            })
                          }
                        >
                          <i className="fa fa-plus me-1"></i> Add Sub-Product
                        </button>
                      </div>
                    )}
                  />
                </div>

                <div className="col-md-12 mt-4">
                  <button
                    className="btn btn-main-primary signbtn mr-2"
                    type="submit"
                    disabled={submitted}
                  >
                    <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                    {t("btn_update")}
                  </button>
                  <button
                    className="btn ripple btn-secondary"
                    type="button"
                    onClick={() => navigate(-1)}
                  >
                    <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                    {t("btn_cancel")}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </FormikProvider>
  );
};

export default EditProduct;
